import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

// usePagination — controlled slice of any array. Resets to page 1 when
// the underlying list shrinks past the current page so users never end up
// stuck on an empty page after filters or deletes.
export function usePagination(items, pageSize) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, page, pageSize]);
  return { page, setPage, totalPages, paged };
}

export default function Pager({ page, setPage, totalPages, testid = "pager" }) {
  if (totalPages <= 1) return null;
  const goto = (p) => setPage(Math.max(1, Math.min(totalPages, p)));
  // Show up to 5 numbered buttons centred around the current page.
  const nums = [];
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div className="flex items-center justify-center gap-1 py-2 font-mono text-xs" data-testid={testid}>
      <Button
        variant="outline" size="icon"
        className="rounded-none border-2 border-[#09090B] h-8 w-8 disabled:opacity-30"
        onClick={() => goto(page - 1)} disabled={page <= 1}
        data-testid={`${testid}-prev`}
      >
        <ChevronLeft size={14} />
      </Button>
      {nums.map((n) => (
        <Button
          key={n}
          variant={n === page ? "default" : "outline"}
          size="sm"
          className={
            n === page
              ? "rounded-none bg-[#09090B] hover:bg-[#0055FF] h-8 w-8 p-0 font-mono"
              : "rounded-none border-2 border-[#09090B] h-8 w-8 p-0 font-mono"
          }
          onClick={() => goto(n)}
          data-testid={`${testid}-page-${n}`}
        >
          {n}
        </Button>
      ))}
      <Button
        variant="outline" size="icon"
        className="rounded-none border-2 border-[#09090B] h-8 w-8 disabled:opacity-30"
        onClick={() => goto(page + 1)} disabled={page >= totalPages}
        data-testid={`${testid}-next`}
      >
        <ChevronRight size={14} />
      </Button>
      <span className="ml-2 text-neutral-500">{page} / {totalPages}</span>
    </div>
  );
}
